import { useEffect, useRef, useState, useMemo } from "react";
import {
  Box, Typography, Select, MenuItem, Divider,
  Button, CircularProgress,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ToolDialog from "../ToolDialog";

const COPIER_URL = "https://app.mapgeoid.com/api-service/copier";

// ─── type helpers ─────────────────────────────────────────────────────────────

// Map schema data_type → canonical group used for compatibility checks
const TYPE_GROUP = {
  integer: "integer",
  string: "string",
  float: "float",
  bool: "bool",
  "list[string]": "list[string]",
  photo: "list[string]",  // photo is an alias for list[string]
};

const TYPE_LABEL = {
  integer: "int",
  string: "str",
  float: "float",
  bool: "bool",
  "list[string]": "str[]",
  photo: "str[]",
};

const TYPE_COLOR = {
  integer: { bg: "#FAEEDA", color: "#633806" },
  string: { bg: "#E6F1FB", color: "#185FA5" },
  float: { bg: "#FAEEDA", color: "#854F0B" },  // slightly different amber from int
  bool: { bg: "#E1F5EE", color: "#085041" },
  "list[string]": { bg: "#EEEDFE", color: "#3C3489" },
  photo: { bg: "#EEEDFE", color: "#3C3489" },
};

function typesCompatible(destType, srcType) {
  return TYPE_GROUP[destType] === TYPE_GROUP[srcType];
}

function TypeBadge({ dtype }) {
  const label = TYPE_LABEL[dtype] ?? dtype;
  const { bg, color } = TYPE_COLOR[dtype] ?? { bg: "rgba(0,0,0,0.06)", color: "inherit" };
  return (
    <Box component="span" sx={{
      fontSize: 10, px: 0.6, py: 0.1, borderRadius: "4px",
      fontFamily: "var(--font-sans)", flexShrink: 0,
      background: bg, color,
    }}>
      {label}
    </Box>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, badge, badgeColor = "optional", children }) {
  return (
    <Box sx={{ px: 1.5, py: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{
          fontSize: 10, fontWeight: 600, textTransform: "uppercase",
          letterSpacing: "0.06em", color: "text.secondary",
        }}>
          {title}
        </Typography>
        {badge && (
          <Typography variant="caption" sx={{
            fontSize: 10, px: 0.75, py: 0.1, borderRadius: "99px", fontWeight: 500,
            background: badgeColor === "required" ? "rgba(211,47,47,0.1)" : "rgba(46,125,50,0.1)",
            color: badgeColor === "required" ? "error.dark" : "success.dark",
          }}>
            {badge}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

// ─── Radio pill group ─────────────────────────────────────────────────────────

function RadioPills({ value, options, onChange }) {
  return (
    <Box sx={{ display: "flex", gap: 0.75 }}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <Box
            key={opt.value}
            onClick={() => onChange(opt.value)}
            sx={{
              display: "flex", alignItems: "center", gap: 0.6,
              px: 1.25, py: 0.4,
              border: "0.5px solid",
              borderColor: active ? "primary.main" : "divider",
              borderRadius: "99px",
              background: active ? "rgba(25,118,210,0.07)" : "action.hover",
              color: active ? "primary.main" : "text.secondary",
              fontSize: 12, cursor: "pointer", userSelect: "none",
              transition: "all 0.15s",
            }}
          >
            <Box sx={{
              width: 10, height: 10, borderRadius: "50%",
              border: "1.5px solid currentColor",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {active && <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: "primary.main", bgcolor: "primary.main" }} />}
            </Box>
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CopierTool({ title, map, layers, onClose }) {

  const [sourceId, setSourceId] = useState("");
  const [destId, setDestId] = useState("");
  const [writeMode, setWriteMode] = useState("append");
  const [fieldMap, setFieldMap] = useState({});   // { dest_key: src_key | "auto" | "" }
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const logsEndRef = useRef(null);

  // ── auto-scroll log ───────────────────────────────────────────────────────
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const pushLog = (type, message) =>
    setLogs((prev) => [...prev, { type, message }]);

  // ── derived layer info ────────────────────────────────────────────────────
  const sourceLayer = layers[sourceId] ?? null;
  const destLayer = layers[destId] ?? null;

  const sourceSchema = sourceLayer?.property_schema ?? [];
  const destSchema = destLayer?.property_schema ?? [];

  const sourceGeom = sourceLayer?.vectorLayer?.get("geometry_type") ?? null;
  const destGeom = destLayer?.vectorLayer?.get("geometry_type") ?? null;

  const geomMatch = sourceGeom && destGeom
    ? sourceGeom.toLowerCase() === destGeom.toLowerCase()
    : null;

  // ── auto-initialise fieldMap when dest schema or source changes ───────────
  useEffect(() => {
    if (!destSchema.length) { setFieldMap({}); return; }

    const srcKeys = sourceSchema.map((f) => f.key);
    const initial = {};
    destSchema.forEach((df) => {
      // pre-fill "auto" if exact key match exists in source
      initial[df.key] = srcKeys.includes(df.key) ? "auto" : "";
    });

    if (sourceId && destId) {
      pushLog("info", "Checking layer schemas…");
      if (geomMatch === true) pushLog("success", `Geometry match — ${sourceGeom}`);
      if (geomMatch === false) pushLog("error", `Geometry mismatch: ${sourceGeom} ≠ ${destGeom}`);
      const autoCount = Object.values(initial).filter((v) => v === "auto").length;
      if (autoCount) pushLog("success", `Auto-matched ${autoCount} field${autoCount > 1 ? "s" : ""}`);
    }

    setFieldMap(initial);
  }, [sourceId, destId]);

  // ── source fields grouped by type (for dropdown filtering) ───────────────
  const sourceByType = useMemo(() => {
    const map = {};
    sourceSchema.forEach((f) => {
      const g = TYPE_GROUP[f.data_type] ?? f.data_type;
      if (!map[g]) map[g] = [];
      map[g].push(f);
    });
    return map;
  }, [sourceSchema]);

  // ── run ───────────────────────────────────────────────────────────────────
  const canRun = !!sourceId && !!destId;

  const handleRun = async () => {
    if (!canRun) return;
    setLogs([]);
    setLoading(true);
    pushLog("info", "Starting copy operation…");

    // Build field_map array — only include rows that have a source selected
    const field_map = Object.entries(fieldMap)
      .filter(([, v]) => v !== "")
      .map(([d_col, s_col]) => ({ [d_col]: s_col }));

    const payload = {
      source: sourceId,
      destination: destId,
      mode: writeMode,
      field_map,
    };

    try {
      const res = await fetch(COPIER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      // ── stream NDJSON ──────────────────────────────────────────────────────
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          try {
            const msg = JSON.parse(trimmed);
            pushLog(msg.type ?? "info", msg.message ?? trimmed);
          } catch {
            pushLog("info", trimmed);
          }
        }
      }
    } catch (err) {
      pushLog("error", `Request failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <ToolDialog title={title} open onClose={onClose} sx={{width : 640}}>

      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)" }}>

        {/* ── LEFT ── */}
        <Box sx={{ borderRight: "0.5px solid", borderColor: "divider", display: "flex", flexDirection: "column" }}>

          <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>

            {/* Source */}
            <Section title="Source layer" badge="Required" badgeColor="required">
              <Select
                fullWidth size="small" displayEmpty
                value={sourceId}
                onChange={(e) => { setSourceId(e.target.value); setLogs([]); }}
                MenuProps={{ style: { zIndex: 2000 } }}
                renderValue={(v) =>
                  v ? (layers[v]?.name ?? v)
                    : <em style={{ color: "rgba(0,0,0,0.38)" }}>— select source layer —</em>
                }
              >
                <MenuItem value=""><em style={{ color: "rgba(0,0,0,0.38)" }}>None</em></MenuItem>
                {Object.entries(layers).map(([id, info]) => (
                  <MenuItem key={id} value={id}>{info.name}</MenuItem>
                ))}
              </Select>
              {sourceGeom && (
                <Box sx={{ mt: 0.6, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <TypeBadge dtype="string" />
                  <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>
                    {sourceGeom}
                  </Typography>
                </Box>
              )}
            </Section>

            <Divider />

            {/* Destination */}
            <Section title="Destination layer" badge="Required" badgeColor="required">
              <Select
                fullWidth size="small" displayEmpty
                value={destId}
                onChange={(e) => { setDestId(e.target.value); setLogs([]); }}
                MenuProps={{ style: { zIndex: 2000 } }}
                renderValue={(v) =>
                  v ? (layers[v]?.name ?? v)
                    : <em style={{ color: "rgba(0,0,0,0.38)" }}>— select destination layer —</em>
                }
              >
                <MenuItem value=""><em style={{ color: "rgba(0,0,0,0.38)" }}>None</em></MenuItem>
                {Object.entries(layers).map(([id, info]) => (
                  <MenuItem key={id} value={id}>{info.name}</MenuItem>
                ))}
              </Select>
              {destGeom && (
                <Box sx={{ mt: 0.6, display: "flex", alignItems: "center", gap: 0.5 }}>
                  {geomMatch === true && <Typography variant="caption" sx={{ fontSize: 11, color: "success.main" }}>✓ Geometry match — {destGeom}</Typography>}
                  {geomMatch === false && <Typography variant="caption" sx={{ fontSize: 11, color: "error.main" }}>✕ Mismatch: {sourceGeom} ≠ {destGeom}</Typography>}
                  {geomMatch === null && <Typography variant="caption" sx={{ fontSize: 11, color: "text.disabled" }}>{destGeom}</Typography>}
                </Box>
              )}
            </Section>

            <Divider />

            {/* Write mode */}
            <Section title="Write mode">
              <RadioPills
                value={writeMode}
                options={[
                  { value: "append", label: "Append" },
                  { value: "overwrite", label: "Overwrite" },
                ]}
                onChange={setWriteMode}
              />
            </Section>

            <Divider />

            {/* Field mapping */}
            {destSchema.length > 0 && (
              <Section title="Field mapping">

                {/* Column headers */}
                <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 18px minmax(0,1fr)", gap: 0.5, mb: 0.75 }}>
                  <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Destination
                  </Typography>
                  <span />
                  <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Source field
                  </Typography>
                </Box>

                {destSchema.map((df) => {
                  const destTypeGroup = TYPE_GROUP[df.data_type] ?? df.data_type;
                  const currentVal = fieldMap[df.key] ?? "";
                  const isAuto = currentVal === "auto";
                  const isNone = currentVal === "";

                  // find selected src field's type for mismatch detection
                  const selectedSrc = sourceSchema.find((f) => f.key === currentVal);
                  const typeMismatch = selectedSrc
                    ? !typesCompatible(df.data_type, selectedSrc.data_type)
                    : false;

                  // select bg state
                  const selBg =
                    isNone ? undefined :
                      isAuto ? "#E1F5EE" :
                        typeMismatch ? "#FAEEDA" : "#E6F1FB";
                  const selColor =
                    isNone ? undefined :
                      isAuto ? "#085041" :
                        typeMismatch ? "#633806" : "#0C447C";
                  const selBorder =
                    isNone ? undefined :
                      isAuto ? "#5DCAA5" :
                        typeMismatch ? "#EF9F27" : "#85B7EB";

                  return (
                    <Box key={df.key} sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0,1fr) 18px minmax(0,1fr)",
                      gap: 0.5, mb: 0.6, alignItems: "center",
                    }}>
                      {/* Destination field pill */}
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: 0.5,
                        height: 28, px: 1,
                        border: "0.5px solid", borderColor: "divider",
                        borderRadius: "var(--border-radius-md)",
                        background: "action.hover",
                        overflow: "hidden",
                      }}>
                        <Typography variant="caption" sx={{
                          fontSize: 12, fontFamily: "var(--font-mono)",
                          flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          color: "text.primary",
                        }}>
                          {df.key}
                        </Typography>
                        <TypeBadge dtype={df.data_type} />
                      </Box>

                      {/* Arrow */}
                      <Typography sx={{ fontSize: 11, color: "text.disabled", textAlign: "center" }}>←</Typography>

                      {/* Source selector */}
                      <Box
                        component="select"
                        value={currentVal}
                        onChange={(e) => setFieldMap((prev) => ({ ...prev, [df.key]: e.target.value }))}
                        sx={{
                          height: 28, width: "100%", fontSize: 12,
                          fontFamily: "var(--font-mono)",
                          px: 0.75, pr: 2.5,
                          border: "0.5px solid",
                          borderColor: selBorder ?? "divider",
                          borderRadius: "var(--border-radius-md)",
                          background: selBg ?? "action.hover",
                          color: selColor ?? "text.primary",
                          appearance: "none",
                          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 3.5l3 3 3-3' stroke='%23888' stroke-width='1.1' stroke-linecap='round'/%3E%3C/svg%3E")`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 6px center",
                          cursor: "pointer",
                          outline: "none",
                        }}
                      >
                        {/* Always-active options */}
                        <option value="">— none —</option>
                        <option value="auto">auto</option>

                        {/* Compatible source fields */}
                        {sourceSchema.length > 0 && (
                          <optgroup label={`— ${destTypeGroup} fields —`}>
                            {sourceSchema
                              .filter((sf) => typesCompatible(df.data_type, sf.data_type))
                              .map((sf) => (
                                <option key={sf.key} value={sf.key}>{sf.key}</option>
                              ))
                            }
                          </optgroup>
                        )}

                        {/* Incompatible source fields — disabled */}
                        {sourceSchema.filter((sf) => !typesCompatible(df.data_type, sf.data_type)).length > 0 && (
                          <optgroup label="— incompatible —">
                            {sourceSchema
                              .filter((sf) => !typesCompatible(df.data_type, sf.data_type))
                              .map((sf) => (
                                <option key={sf.key} value={sf.key} disabled>
                                  {sf.key} ({TYPE_LABEL[sf.data_type] ?? sf.data_type})
                                </option>
                              ))
                            }
                          </optgroup>
                        )}
                      </Box>

                      {/* Type mismatch warning */}
                      {typeMismatch && (
                        <Box sx={{ gridColumn: "3", mt: -0.4 }}>
                          <Typography variant="caption" sx={{ fontSize: 10, color: "warning.dark" }}>
                            ⚠ type mismatch
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Section>
            )}

          </Box>

          {/* Run button — sticky at bottom */}
          <Box sx={{ px: 1.5, py: 1.25, borderTop: "0.5px solid", borderColor: "divider", mt: "auto" }}>
            {!canRun && (
              <Typography variant="caption" color="text.disabled" display="block" mb={0.75}>
                Select source and destination layers to run
              </Typography>
            )}
            <Button
              fullWidth variant="contained" size="small"
              disabled={!canRun || loading || geomMatch === false}
              onClick={handleRun}
              startIcon={loading
                ? <CircularProgress size={13} color="inherit" />
                : <PlayArrowIcon fontSize="small" />
              }
            >
              {loading ? "Copying…" : "Run"}
            </Button>
          </Box>
        </Box>

        {/* ── RIGHT: LOG ── */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 1.5, py: 1, borderBottom: "0.5px solid", borderColor: "divider",
          }}>
            <Typography variant="caption" sx={{
              fontSize: 10, fontWeight: 600, textTransform: "uppercase",
              letterSpacing: "0.06em", color: "text.secondary",
            }}>
              Log
            </Typography>
            <Button size="small" variant="text"
              onClick={() => setLogs([])}
              sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: "none", color: "text.disabled" }}
            >
              Clear
            </Button>
          </Box>

          <Box sx={{
            flex: 1, px: 1.5, py: 1, minHeight: 120,
            maxHeight: "60vh", overflowY: "auto",
            display: "flex", flexDirection: "column", gap: 0.3,
          }}>
            {logs.length === 0 ? (
              <Typography variant="caption" sx={{ fontSize: 11, color: "text.disabled", fontStyle: "italic" }}>
                Run to see logs…
              </Typography>
            ) : logs.map((log, i) => (
              <Box key={i} sx={{ display: "flex", gap: 0.6, alignItems: "flex-start" }}>
                <Typography variant="caption" sx={{
                  fontSize: 11, flexShrink: 0, mt: "1px",
                  color: log.type === "error" ? "error.main" :
                    log.type === "success" ? "success.main" :
                      log.type === "warn" ? "warning.dark" : "text.disabled",
                }}>
                  {log.type === "error" ? "✕" : log.type === "success" ? "✓" : log.type === "warn" ? "⚠" : "›"}
                </Typography>
                <Typography variant="caption" sx={{
                  fontSize: 11, fontFamily: "var(--font-mono)", lineHeight: 1.5,
                  color: log.type === "error" ? "error.main" :
                    log.type === "success" ? "success.main" :
                      log.type === "warn" ? "warning.dark" : "text.secondary",
                }}>
                  {log.message}
                </Typography>
              </Box>
            ))}
            <div ref={logsEndRef} />
          </Box>
        </Box>

      </Box>
    </ToolDialog>
  );
}
